import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Shield, Star, Crown, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MembershipCardForm } from '@/components/ui/membership-card-form';

// Imágenes de tarjetas de membresía
import cardBronce from '@/assets/membresia-logos/card-bronce.png';
import cardSilver from '@/assets/membresia-logos/card-silver.png';
import cardGold from '@/assets/membresia-logos/card-gold.png';
import logoBronce from '@/assets/membresia-logos/logo-bronce-new.png';
import logoPlata from '@/assets/membresia-logos/logo-plata-new.png';
import logoOro from '@/assets/membresia-logos/logo-oro-new.png';
import cardLogoBronce from '@/assets/membresia-logos/card-logo-bronce.png';
import cardLogoPlata from '@/assets/membresia-logos/card-logo-plata.png';
import cardLogoOro from '@/assets/membresia-logos/card-logo-oro.png';
import phoneBronceClear from '@/assets/phones/phone-bronce-clear.png';
import phonePlataMagsafe from '@/assets/phones/phone-plata-magsafe.png';
import phoneOroOtterbox from '@/assets/phones/phone-oro-otterbox.png';

const formatPrice = (price: number) => `₡${price.toLocaleString('es-CR')}`;

// Descuentos visuales por categoría con imágenes
const discountVisuals = {
  bronce: [
    { category: 'Gaming', discount: '5%', image: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
    { category: 'Estuches', discount: '5%', image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
    { category: 'Tecnología', discount: '5%', image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
    { category: 'Cargadores', discount: '5%', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
    { category: 'Audio', discount: '5%', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
    { category: 'Reparaciones', discount: '10%', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=80&h=80&fit=crop', color: 'from-amber-700 to-amber-900' },
  ],
  plata: [
    { category: 'Gaming', discount: '10%', image: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
    { category: 'Estuches', discount: '10%', image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
    { category: 'Tecnología', discount: '10%', image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
    { category: 'Cargadores', discount: '10%', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
    { category: 'Audio', discount: '10%', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
    { category: 'Reparaciones', discount: '20%', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=80&h=80&fit=crop', color: 'from-slate-400 to-slate-600' },
  ],
  oro: [
    { category: 'Gaming', discount: '20%', image: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
    { category: 'Estuches', discount: '20%', image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
    { category: 'Tecnología', discount: '20%', image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
    { category: 'Cargadores', discount: '20%', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
    { category: 'Audio', discount: '20%', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
    { category: 'Reparaciones', discount: '30%', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=80&h=80&fit=crop', color: 'from-yellow-500 to-amber-600' },
  ],
};

const plans = [
  {
    id: 'bronce',
    name: 'Bronce',
    subtitle: 'Protección Básica',
    regularPrice: 15000,
    launchPrice: 12000,
    annualPrice: 120000,
    color: 'amber',
    icon: Shield,
    logo: logoBronce,
    cardLogo: cardLogoBronce,
    cardImage: cardBronce,
    discounts: discountVisuals.bronce,
    features: [
      { text: '1 Temperado sencillo o Hidrogel HD por mes', included: true },
      { text: '1 Estuche Space por mes', included: true },
      { text: '2 Limpiezas por mes', included: true },
      { text: 'Garantía de Temperado 24h', included: true },
      { text: 'Atención preferencial', included: false },
      { text: 'Envío gratis', included: false },
      { text: 'WhatsApp VIP', included: false },
    ],
    message: 'Protegés tu celular todos los meses y todo te sale más barato.',
  },
  {
    id: 'plata',
    name: 'Plata',
    subtitle: 'Protección Completa',
    regularPrice: 30000,
    launchPrice: 27000,
    annualPrice: 270000,
    color: 'slate',
    icon: Star,
    logo: logoPlata,
    cardLogo: cardLogoPlata,
    cardImage: cardSilver,
    popular: true,
    discounts: discountVisuals.plata,
    immediateBenefits: [
      { highlight: '2', text: 'Temperados o Hidrogel Mate/Restaurativo', subtext: 'cada mes' },
      { highlight: '2', text: 'Estuches Space MagSafe', subtext: 'cada mes' },
      { highlight: '20%', text: 'Reparaciones', subtext: 'en todas' },
    ],
    features: [
      { text: '2 Temperados completos o 2 Hidrogel Mate o Restaurativo por mes', included: true },
      { text: '2 Estuches Space MagSafe por mes', included: true },
      { text: '2 Limpiezas por mes', included: true },
      { text: 'Garantía de Temperado 3 días', included: true },
      { text: 'Atención preferencial', included: true },
      { text: 'Envío gratis +₡40.000', included: true },
      { text: 'WhatsApp VIP', included: false },
    ],
    message: 'Pagás menos en cada compra y tenés beneficios reales todos los meses.',
  },
  {
    id: 'oro',
    name: 'Oro',
    subtitle: 'VIP Total',
    regularPrice: 50000,
    launchPrice: 45000,
    annualPrice: 450000,
    color: 'yellow',
    icon: Crown,
    logo: logoOro,
    cardLogo: cardLogoOro,
    cardImage: cardGold,
    vip: true,
    discounts: discountVisuals.oro,
    features: [
      { text: '2 Temperados completo Privado o 2 Hidrogel Premium por mes', included: true },
      { text: '1 Estuche Premium o MagSafe por mes', included: true },
      { text: '3 Limpiezas por mes', included: true },
      { text: 'Garantía de Temperado 5 días', included: true },
      { text: 'Atención VIP exclusiva', included: true },
      { text: 'Envío gratis +₡20.000', included: true },
      { text: 'WhatsApp personalizado', included: true },
    ],
    message: 'Tenés línea directa con VillaTech y beneficios que nadie más tiene.',
  }
];

export const MembershipPlansSection: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openCardForm = (plan: any) => {
    setSelectedPlan(plan);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (formData: any, plan: any) => {
    const whatsappNumber = "50688888888";
    const message = encodeURIComponent(
      `¡Hola! Quiero activar mi membresía ${plan.name} de VillaTech.\n\n` +
      `📋 Mis datos:\n` +
      `👤 Nombre: ${formData.nombre}\n` +
      `📱 WhatsApp: ${formData.telefono}\n` +
      `📧 Email: ${formData.email}\n` +
      `🆔 Cédula: ${formData.cedula}\n` +
      `📲 Celular: ${formData.modeloCelular}`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <section id="planes" className="py-20 bg-gradient-to-b from-black to-zinc-900">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="text-blue-400">Elegí tu membresía</span>
          </h2>
          <p className="text-zinc-400 text-lg mb-6">Precios promocionales hasta el 31 de enero 2026</p>
          
          {/* Toggle Mensual / Anual */}
          <div className="flex justify-center items-center gap-4 mb-8">
            <span className={`text-lg font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-blue-400' : 'text-zinc-500'}`}>
              Mensual
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-20 h-10 rounded-full bg-zinc-800 border border-zinc-600 p-1 transition-colors"
            >
              <motion.div
                className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 shadow-lg"
                animate={{ x: billingPeriod === 'annual' ? 40 : 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-medium transition-colors ${billingPeriod === 'annual' ? 'text-blue-400' : 'text-zinc-500'}`}>
                Anual
              </span>
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40 text-xs">
                2 meses gratis
              </Badge>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-3xl overflow-hidden ${
                  plan.vip 
                    ? 'bg-gradient-to-b from-blue-500/20 to-zinc-900 border-2 border-blue-500/50 shadow-[0_0_40px_rgba(59,130,246,0.2)]' 
                    : 'bg-zinc-900 border border-zinc-700'
                }`}
              >
                {/* Badge */}
                {plan.popular && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-slate-400 text-black px-4 py-1 font-bold rounded-b-lg rounded-t-none">
                      MÁS POPULAR
                    </Badge>
                  </div>
                )}
                {plan.vip && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-4 py-1 font-bold rounded-b-lg rounded-t-none shadow-lg">
                      50 DISPONIBLES
                    </Badge>
                  </div>
                )}

                <div className="p-8 pt-12">
                  {/* Card Image */}
                  <div className="mb-8">
                    <div className="relative mx-auto max-w-[280px] aspect-[1.6/1] bg-transparent overflow-hidden rounded-2xl">
                      <img 
                        src={plan.cardImage} 
                        alt={`Membresía ${plan.name}`}
                        className="w-full h-full object-contain drop-shadow-[0_15px_30px_rgba(0,0,0,0.5)]"
                        style={{ background: 'transparent' }}
                      />
                    </div>
                    <p className="text-zinc-400 text-center mt-3">{plan.subtitle}</p>
                  </div>

                  {/* Precio */}
                  <div className="text-center mb-6">
                    {billingPeriod === 'monthly' ? (
                      <>
                        <div className="text-zinc-500 line-through text-lg">{formatPrice(plan.regularPrice)}/mes</div>
                        <div className="text-4xl font-bold text-white">{formatPrice(plan.launchPrice)}</div>
                        <div className="text-zinc-400">/mes</div>
                      </>
                    ) : (
                      <>
                        <div className="text-zinc-500 line-through text-lg">{formatPrice(plan.launchPrice * 12)}/año</div>
                        <div className="text-4xl font-bold text-white">{formatPrice(plan.annualPrice)}</div>
                        <div className="text-zinc-400">/año</div>
                        <Badge className="mt-2 bg-blue-500/20 text-blue-400 border-blue-500/40">
                          Ahorrás {formatPrice(plan.launchPrice * 12 - plan.annualPrice)}
                        </Badge>
                      </>
                    )}
                  </div>

                  {/* Discount Categories with Images */}
                  <div className="mb-8">
                    <p className="text-base text-zinc-300 text-center mb-4 font-semibold">
                      Tus descuentos:
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {plan.discounts?.slice(0, 6).map((discount: any, i: number) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          whileInView={{ opacity: 1, scale: 1, y: 0 }}
                          whileHover={{ scale: 1.15, y: -8, zIndex: 10 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.08, type: "spring", stiffness: 400, damping: 15 }}
                          className="relative group cursor-pointer"
                        >
                          <motion.div 
                            className={`relative rounded-2xl overflow-hidden aspect-square 
                              shadow-[0_8px_30px_rgba(0,0,0,0.4)] 
                              border-2 border-white/10
                              group-hover:shadow-[0_15px_40px_rgba(0,0,0,0.5)]
                              transition-shadow duration-300`}
                            animate={{ y: [0, -6, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                          >
                            <img 
                              src={discount.image} 
                              alt={discount.category}
                              className="w-full h-full object-cover scale-110"
                            />
                            <div className={`absolute inset-0 bg-gradient-to-t ${discount.color} opacity-85`} />
                            <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                              <span className="text-white font-black text-2xl md:text-3xl drop-shadow-[0_3px_6px_rgba(0,0,0,0.9)]">
                                {discount.discount}
                              </span>
                              <span className="text-white text-[10px] md:text-xs font-bold text-center leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] mt-1">
                                {discount.category}
                              </span>
                            </div>
                          </motion.div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Phone Compatibility Visual */}
                  <div className="mb-6 flex items-center justify-center">
                    <div className="p-4 rounded-3xl bg-zinc-800/50 shadow-[inset_4px_4px_8px_rgba(0,0,0,0.4),inset_-4px_-4px_8px_rgba(255,255,255,0.05),8px_8px_16px_rgba(0,0,0,0.3),-4px_-4px_12px_rgba(255,255,255,0.03)] border border-zinc-700/30">
                      <img
                        src={plan.name.toLowerCase().includes('oro') ? phoneOroOtterbox : plan.name.toLowerCase().includes('plata') ? phonePlataMagsafe : phoneBronceClear}
                        alt={plan.name.toLowerCase().includes('oro') ? 'iPhone con OtterBox' : plan.name.toLowerCase().includes('plata') ? 'iPhone con MagSafe' : 'iPhone con estuche transparente'}
                        className="w-full max-w-[260px] h-[160px] object-contain rounded-2xl"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>

                  {/* Immediate Benefits for Plata */}
                  {plan.immediateBenefits && (
                    <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-slate-500/20 to-slate-600/10 border border-slate-400/30">
                      <p className="text-center text-slate-300 font-bold mb-4 text-sm uppercase tracking-wide">
                        ⚡ Lo que recibís de inmediato
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {plan.immediateBenefits.map((benefit: any, i: number) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="text-center p-3 rounded-xl bg-slate-500/20 border border-slate-400/20"
                          >
                            <div className="text-2xl md:text-3xl font-black text-slate-300 drop-shadow-lg">
                              {benefit.highlight}
                            </div>
                            <div className="text-xs font-bold text-white mt-1">
                              {benefit.text}
                            </div>
                            <div className="text-[10px] text-slate-400">
                              {benefit.subtext}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature: any, i: number) => (
                      <li key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={feature.included ? 'text-white' : 'text-zinc-500'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Mensaje comercial */}
                  <p className="text-sm text-zinc-400 text-center mb-6 italic">
                    "{plan.message}"
                  </p>

                  {/* CTA */}
                  <Button
                    onClick={() => openCardForm(plan)}
                    className={`w-full py-6 text-lg font-bold rounded-xl ${
                      plan.vip
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-600'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Activar Membresía
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Card Form Modal */}
      {selectedPlan && (
        <MembershipCardForm
          plan={selectedPlan}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </section>
  );
};